import React, { useContext, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../components/Navbar";
import emailjs from "emailjs-com";
import Scale from "../assets/images/scale.webp";
import "../styles/AIBEPreparation.css";
import HomeContactSection from "../components/HomeContactSection";
import { Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../App";
import {
  BareActsGallery,
  ExamStructureSection,
  PrioritySyllabusSection,
  TacticsSection,
  TrafficLightStrategy,
  ExamChecklistGrid,
  PracticeCentre,
} from "../components/aibe/AIBEComponents";
import { useAibeNavigation } from "../components/aibe/aibeNavigation";

import { aibeSubjects, aibePreviousPapers, aibeOfficialSyllabus } from "../data/aibeModuleData";
import { paperQuestions } from "../data/aibeQuestionRegistries";

function AIBEPreparation() {
  const { subpage } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const aibeNavigation = useAibeNavigation();
  const { currentUser, loading: authLoading } = useContext(AuthContext);
  const requireAibeAuth = (target = location) => {
    if (authLoading) return false;
    if (currentUser) return true;
    navigate("/auth", { state: { from: target } });
    return false;
  };

  // Form State for Contact Section
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    time: new Date().toLocaleString(),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(false);
    setError(false);

    const templateParams = {
      name: formData.name,
      email: formData.email,
      message: formData.message,
      time: new Date().toLocaleString(),
    };

    emailjs
      .send(
        "service_TejCodex",
        "template_9w26kov",
        templateParams,
        "-1wDk-Z48uDBTDyvr"
      )
      .then(
        () => {
          setIsSubmitting(false);
          setSuccess(true);
          setFormData({ name: "", email: "", message: "" });
        },
        () => {
          setIsSubmitting(false);
          setError(true);
        }
      );
  };

  useEffect(() => {
    let timer;
    if (success || error) {
      timer = setTimeout(() => {
        setSuccess(false);
        setError(false);
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [success, error]);

  const courses = [
    {
      id: "syllabus",
      title: "Syllabus",
      comment: "Official Syllabus & Weightage",
      onClick: () => {
        aibeNavigation.pushAibeState({ screen: "syllabus" });
      },
    },
    {
      id: "practice",
      title: "Practice",
      comment: "Interactive MCQs & Past Papers",
      onClick: () => {
        const target = { pathname: "/aibe-preparation/practice" };
        if (requireAibeAuth(target)) {
          aibeNavigation.pushAibeState({ screen: "practice", view: "setup" });
        }
      },
    },
  ];

  // Helper for contact section rendering across views
  const renderContactAndFooter = () => (
    <>
      <HomeContactSection
        scale={Scale}
        formData={formData}
        loading={isSubmitting}
        success={success}
        error={error}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />

      {/* Footer Section */}
      <footer className="footer-section">
        <div className="footer-top">
          <img src={Scale} alt="Scale" className="footer-logo" />
          <span className="footer-title">TejCodex</span>
          <span className="footer-rights">© 2025 All rights reserved.</span>
        </div>

        <div className="footer-links">
          <a href="/">Home</a> |<a href="/notes"> Notes</a> |
          <a href="/pyqs"> PYQs</a> |<a href="/aibe-preparation"> AIBE</a> | <a href="/about"> About Us</a> |
          <a href="/contact"> Contact Us</a>
        </div>
      </footer>
    </>
  );

  // Render Sub-Page: Syllabus for AIBE
  if (subpage === "syllabus") {
    return (
      <div className="notes aibe-page">
        <Helmet>
          <title>Official AIBE Syllabus & Weightage | TejCodex</title>
          <meta
            name="description"
            content="Review the AIBE syllabus, subject-wise weightage, and Bare Act list for All India Bar Examination preparation on TejCodex."
          />
          <link rel="canonical" href="https://tejcodex.com/aibe-preparation/syllabus" />
          <meta property="og:title" content="AIBE Syllabus and Bare Act List | TejCodex" />
          <meta property="og:description" content="AIBE syllabus, subject weightage, and Bare Acts for focused All India Bar Examination preparation." />
          <meta property="og:url" content="https://tejcodex.com/aibe-preparation/syllabus" />
        </Helmet>

        <Navbar />

        {/* Intro Section: Trapeziums */}
        <div className="Nmsg-trapezium">
          <div className="Nmsg-trapezium-black1"></div>
          <div className="Nmsg-trapezium-white1"></div>
        </div>
        <div className="Nmsg-trapezium-black2"></div>
        <div className="Nmsg-trapezium-white2"></div>

        <div className="aibe-back-container">
          <button
            type="button"
            className="aibe-back-btn"
            onClick={() => aibeNavigation.backAibe({ screen: "home" })}
          >
            Back
          </button>
        </div>
        <h1 className="aibe-block-aheading">AIBE Syllabus</h1>
        <p className="aibe-block-asubheading">
          Review the official syllabus and open Bare Acts in a separate browser tab.
        </p>
        <div className="aibe-section-block">
          <div className="aibe-official-syllabus-link">
            <h2 className="aibe-block-heading">Official AIBE Syllabus</h2>

            <p className="aibe-block-subheading">
              Subject-wise distribution of questions as provided in the official AIBE syllabus.
            </p>

            <div className="aibe-syllabus-table-wrapper">
              <table className="aibe-syllabus-table">
                <thead>
                  <tr>
                    <th>Sr. No.</th>
                    <th>Topic / Subject</th>
                    <th>Number of Questions</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td><strong>1.</strong></td>
                    <td>Constitutional law</td>
                    <td>10</td>
                  </tr>

                  <tr>
                    <td><strong>2.</strong></td>
                    <td>
                      I. P. C. (Indian Penal Code) &amp; (New) Bharatiya Nyaya Sanhita
                    </td>
                    <td>8</td>
                  </tr>

                  <tr>
                    <td><strong>3.</strong></td>
                    <td>
                      Cr. P. C. (Criminal Procedure Code) &amp; (New) Bharatiya Nagarik
                      Suraksha Sanhita
                    </td>
                    <td>10</td>
                  </tr>

                  <tr>
                    <td><strong>4.</strong></td>
                    <td>C. P. C. (Code of Civil Procedure)</td>
                    <td>10</td>
                  </tr>

                  <tr>
                    <td><strong>5.</strong></td>
                    <td>
                      Evidence Act &amp; (New) Bharatiya Sakshya Adhiniyam
                    </td>
                    <td>8</td>
                  </tr>

                  <tr>
                    <td><strong>6.</strong></td>
                    <td>Alternative Dispute Redressal including Arbitration Act</td>
                    <td>4</td>
                  </tr>

                  <tr>
                    <td><strong>7.</strong></td>
                    <td>Family Law</td>
                    <td>8</td>
                  </tr>

                  <tr>
                    <td><strong>8.</strong></td>
                    <td>Public Interest Litigation</td>
                    <td>4</td>
                  </tr>

                  <tr>
                    <td><strong>9.</strong></td>
                    <td>Administration Law</td>
                    <td>3</td>
                  </tr>

                  <tr>
                    <td><strong>10.</strong></td>
                    <td>
                      Professional Ethics &amp; Cases of Professional Misconduct under
                      Bar Council of India Rules
                    </td>
                    <td>4</td>
                  </tr>

                  <tr>
                    <td><strong>11.</strong></td>
                    <td>Company Law</td>
                    <td>2</td>
                  </tr>

                  <tr>
                    <td><strong>12.</strong></td>
                    <td>Environmental Law</td>
                    <td>2</td>
                  </tr>

                  <tr>
                    <td><strong>13.</strong></td>
                    <td>Cyber Law</td>
                    <td>2</td>
                  </tr>

                  <tr>
                    <td><strong>14.</strong></td>
                    <td>Labour &amp; Industrial Law</td>
                    <td>4</td>
                  </tr>

                  <tr>
                    <td><strong>15.</strong></td>
                    <td>
                      Law of Tort, including Motor Vehicle Act and Consumer Protection Law
                    </td>
                    <td>5</td>
                  </tr>

                  <tr>
                    <td><strong>16.</strong></td>
                    <td>Law related to Taxation</td>
                    <td>4</td>
                  </tr>

                  <tr>
                    <td><strong>17.</strong></td>
                    <td>
                      Law of Contract, Specific Relief, Property Laws, Negotiable
                      Instrument Act
                    </td>
                    <td>8</td>
                  </tr>

                  <tr>
                    <td><strong>18.</strong></td>
                    <td>Land Acquisition Act</td>
                    <td>2</td>
                  </tr>

                  <tr>
                    <td><strong>19.</strong></td>
                    <td>Intellectual Property Laws</td>
                    <td>2</td>
                  </tr>

                  <tr className="aibe-syllabus-total-row">
                    <td></td>
                    <td><strong>Total</strong></td>
                    <td><strong>100</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="aibe-official-pdf-note">
              For complete official details, refer to the official AIBE syllabus PDF.
            </p>

            <a
              href={aibeOfficialSyllabus.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="aibe-cta-btn"
            >
              Open Official AIBE Syllabus PDF
            </a>
          </div>
        </div>
        <div className="aibe-section-block">

          <BareActsGallery subjects={aibeSubjects} />

          {/* Center Practice Option Button */}
          <div className="aibe-center-action-row">
            <button
              type="button"
              className="aibe-cta-btn"
              onClick={() => {
                const target = { pathname: "/aibe-preparation/practice" };
                if (requireAibeAuth(target)) {
                  aibeNavigation.pushAibeState({ screen: "practice", view: "setup" });
                }
              }}
            >
              🎯 Open Practice Page
            </button>
          </div>
        </div>

        {renderContactAndFooter()}
      </div>
    );
  }

  // Render Sub-Page: Practice Options
  if (subpage === "practice") {
    if (authLoading) {
      return (
        <div className="notes aibe-page">
          <Navbar />
          <p className="aibe-empty-state">Checking login...</p>
        </div>
      );
    }

    if (!currentUser) {
      return <Navigate to="/auth" state={{ from: location }} replace />;
    }

    return (
      <div className="notes aibe-page">
        <Helmet>
          <title>AIBE Practice Online - MCQs, Bare Acts & PYQs | TejCodex</title>
          <meta
            name="description"
            content="Practice AIBE questions online with subject-wise MCQs, Bare Act reference, previous year papers, explanations, timers, and score tracking."
          />
          <link rel="canonical" href="https://tejcodex.com/aibe-preparation/practice" />
          <meta property="og:title" content="AIBE Practice Online | TejCodex" />
          <meta property="og:description" content="AIBE practice questions, Bare Acts, previous year papers, explanations, and timed sessions for law students." />
          <meta property="og:url" content="https://tejcodex.com/aibe-preparation/practice" />
        </Helmet>

        <Navbar />

        {/* Intro Section: Trapeziums */}
        <div className="Nmsg-trapezium">
          <div className="Nmsg-trapezium-black1"></div>
          <div className="Nmsg-trapezium-white1"></div>
        </div>
        <div className="Nmsg-trapezium-black2"></div>
        <div className="Nmsg-trapezium-white2"></div>

        <div className="aibe-back-container">
          <button
            type="button"
            className="aibe-back-btn"
            onClick={() => aibeNavigation.backAibe({ screen: "home" })}
          >
            Back
          </button>
        </div>
        <h1 className="aibe-block-aheading">Practice</h1>
        <p className="aibe-block-asubheading">
          Test your knowledge with past AIBE question papers and random practice.
        </p>
        <div className="aibe-section-block">
          <PracticeCentre
            subjects={aibeSubjects}
            papers={aibePreviousPapers}
            paperQuestions={paperQuestions}
            aibeNavigation={aibeNavigation}
          />
        </div>

        {renderContactAndFooter()}
      </div>
    );
  }

  // Default Main AIBE Preparation Hub page
  return (
    <div className="notes aibe-page">
      <Helmet>
        <title>AIBE Preparation Online - Syllabus, Practice & Bare Acts | TejCodex</title>
        <meta
          name="description"
          content="Prepare for AIBE online with TejCodex. Access the AIBE syllabus, Bare Acts, previous year questions, timed practice, explanations, and exam-focused study material."
        />
        <link rel="canonical" href="https://tejcodex.com/aibe-preparation" />
        <meta name="keywords" content="AIBE preparation, AIBE syllabus, AIBE practice, AIBE Bare Acts, AIBE previous year questions, AIBE study material" />
        <meta property="og:title" content="AIBE Preparation Online | TejCodex" />
        <meta property="og:description" content="Student-focused AIBE syllabus, Bare Acts, practice questions, and previous year paper preparation on TejCodex." />
        <meta property="og:url" content="https://tejcodex.com/aibe-preparation" />
        <meta property="og:image" content="https://tejcodex.com/preview.webp" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "AIBE Preparation",
          "description": "AIBE syllabus, Bare Acts, practice questions, previous year papers, explanations, and exam preparation resources for law students.",
          "provider": {
            "@type": "Organization",
            "name": "TejCodex",
            "sameAs": "https://tejcodex.com"
          },
          "url": "https://tejcodex.com/aibe-preparation",
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "courseWorkload": "Self-paced"
          },
          "about": aibeSubjects.map((subject) => ({
            "@type": "Thing",
            "name": subject.name,
            "description": `${subject.name} Bare Act for AIBE preparation`
          }))
        })}</script>
      </Helmet>

      <Navbar />

      {/* Intro Section: Trapeziums + Heading & Subheading (Notes page style) */}
      <div className="Nmsg-trapezium">
        <div className="Nmsg-trapezium-black1"></div>
        <div className="Nmsg-trapezium-white1"></div>
      </div>
      <div className="Nmsg-trapezium-black2"></div>
      <div className="Nmsg-trapezium-white2"></div>

      <h1 className="notes-heading">AIBE</h1>
      <p className="notes-subheading">
        Review Syllabus, Bare Acts and Practice MCQs.
      </p>

      {/* Coursewise Section for AIBE Syllabus & Practice Options */}
      <section className="acoursewise-section" id="aibe-syllabus-container">
        <h2 className="acoursewise-heading">Start Preparation...</h2>
        <p className="aibe-seo-intro">
          TejCodex brings AIBE preparation into one place for law students: official syllabus guidance, Bare Act references, previous year question practice, timed MCQs, answer checking, and explanations.
        </p>
        <div className="acourse-row">
          {courses.map((course, index) => (
            <div
              key={index}
              className={`acourse-container ${index === 1 ? "course-large" : "course-small"}`}
              style={{ cursor: "pointer" }}
              onClick={course.onClick}
            >
              <div className="acourse-spine"></div>

              <div className="acourse-cover">
                <div className="acourse-title-rect">
                  <img src={Scale} alt="Scale" className="acourse-icon" />
                  <span className="acourse-title">{course.title}</span>
                  <span className="acourse-comment">{course.comment}</span>
                </div>
              </div>

              <div className="acourse-page">
                <p>{course.comment}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section 3: Understanding the Exam Pattern & Structure */}
      <ExamStructureSection />

      {/* Section 4: Prioritise Syllabus & High Yield Subjects */}
      <PrioritySyllabusSection />

      {/* Section 5: Examination Tactics Details */}
      <TacticsSection />

      {/* Section 6: Traffic Light Strategy for AIBE */}
      <TrafficLightStrategy />

      {/* Section 7: Exam Day Checklist (No checkbox boxes, pure detail cards) */}
      <ExamChecklistGrid />

      {renderContactAndFooter()}
    </div>
  );
}

export default AIBEPreparation;
